import { Link } from "react-router";
import { ArrowLeft, ArrowRight, Save, CheckCircle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { useIntakeForm } from "./hooks/useIntakeForm";
import { StepIndicator } from "./components/StepIndicator";
import { PersonTypeSelector } from "./components/PersonTypeSelector";
import { DemographicsStep } from "./components/DemographicsStep";
import { BackgroundStep } from "./components/BackgroundStep";
import { AdditionalStep } from "./components/AdditionalStep";
import { MentalHealthStep } from "./components/MentalHealthStep";

export function IntakeWizard() {
  const {
    form,
    currentStep,
    totalSteps,
    steps,
    canGoNext,
    canGoBack,
    goNext,
    goBack,
    goToStep,
    saveIncomplete,
    submit,
    isSubmitting,
    submitError,
    dropdowns,
    isLoadingDropdowns,
    customFields,
    isLoadingCustomFields,
    personType,
    setPersonType,
    hasDraft,
    clearFormDraft,
    ssnDuplicateWarning,
    checkSsnDuplicate,
    lookupZipCode,
  } = useIntakeForm();

  const stepIndex = steps.indexOf(currentStep);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/clients">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              New Client Intake
            </h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Complete the intake form to register a new client
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasDraft && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearFormDraft}
              className="gap-1.5 text-muted-foreground"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Clear Draft
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={() => void saveIncomplete()}
            loading={isSubmitting}
            className="gap-1.5"
          >
            <Save className="h-4 w-4" />
            Save Incomplete
          </Button>
        </div>
      </div>

      {/* Draft restored notice */}
      {hasDraft && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <p className="text-sm text-blue-800">
            A previously saved draft has been restored. You can continue where
            you left off, or clear the draft to start fresh.
          </p>
        </div>
      )}

      {/* Person Type Selector */}
      <Card>
        <CardContent className="p-6">
          <PersonTypeSelector
            value={personType}
            onChange={setPersonType}
            disabled={isSubmitting}
          />
        </CardContent>
      </Card>

      {/* Step Indicator */}
      <div className="rounded-lg border bg-card p-4">
        <StepIndicator
          steps={steps}
          currentStep={currentStep}
          onStepClick={goToStep}
        />
      </div>

      {/* Step Content */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canGoNext) {
            void goNext();
          } else {
            void submit();
          }
        }}
      >
        {currentStep === 1 && (
          <DemographicsStep
            form={form}
            dropdowns={dropdowns}
            isLoadingDropdowns={isLoadingDropdowns}
            ssnDuplicateWarning={ssnDuplicateWarning}
            onSsnBlur={checkSsnDuplicate}
            onZipBlur={lookupZipCode}
            personType={personType}
          />
        )}

        {currentStep === 2 && (
          <BackgroundStep
            form={form}
            dropdowns={dropdowns}
            isLoadingDropdowns={isLoadingDropdowns}
          />
        )}

        {currentStep === 3 && (
          <AdditionalStep
            form={form}
            dropdowns={dropdowns}
            customFields={customFields}
            isLoadingCustomFields={isLoadingCustomFields}
            personType={personType}
          />
        )}

        {currentStep === 4 && <MentalHealthStep form={form} />}

        {/* Error Message */}
        {submitError && (
          <div className="mt-4 rounded-lg border border-destructive bg-destructive/10 px-4 py-3">
            <p className="text-sm text-destructive">{submitError}</p>
          </div>
        )}

        {/* Navigation */}
        <div className="mt-8 flex items-center justify-between border-t pt-6">
          <div>
            {canGoBack && (
              <Button
                type="button"
                variant="outline"
                onClick={goBack}
                disabled={isSubmitting}
                className="gap-1.5"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              Step {stepIndex + 1} of {totalSteps}
            </span>

            {canGoNext ? (
              <Button
                type="submit"
                className="gap-1.5"
                disabled={isSubmitting}
              >
                Next
                <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="submit"
                loading={isSubmitting}
                className="gap-1.5"
              >
                <CheckCircle className="h-4 w-4" />
                Submit Intake
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
